import React, { useState } from 'react';
import { triggerRefiAccounting, bulkTriggerRefiAccounting } from '../services/api';

const ManualTrigger = ({ onTrigger }) => {
  const [formData, setFormData] = useState({
    new_loan_id: '',
    old_loan_number: '',
    refi_payoff: '',
    refi_party: 'Panacea Fund I, LLC',
  });
  
  const [bulkData, setBulkData] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.new_loan_id || !formData.old_loan_number || !formData.refi_payoff || !formData.refi_party) {
      alert('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const payload = {
        ...formData,
        new_loan_id: parseInt(formData.new_loan_id),
        refi_payoff: parseFloat(formData.refi_payoff),
        knackly_data: {
          data: {
            TaxLender: formData.refi_party,
            StandardFees: {
              RefiParty: formData.refi_party,
              RefiPayoff: parseFloat(formData.refi_payoff),
              RefiLoanNumber: formData.old_loan_number,
            },
          },
        },
      };
      
      const response = await triggerRefiAccounting(payload);
      
      setResult({
        success: response.success,
        message: response.message,
      });
      
      // Clear form on success
      if (response.success) {
        setFormData({
          new_loan_id: '',
          old_loan_number: '',
          refi_payoff: '',
          refi_party: 'Panacea Fund I, LLC',
        });
        
        // Refresh parent data
        if (onTrigger) onTrigger();
      }
      
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to trigger refi',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (!bulkData.trim()) {
      alert('Please enter bulk data');
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      // Parse bulk data (expected format: CSV or JSON array)
      const lines = bulkData.trim().split('\n').filter(line => line.trim());
      const refis = [];
      
      for (const line of lines) {
        const parts = line.split(',').map(p => p.trim());
        
        if (parts.length < 4) {
          alert(`Invalid line: ${line}\nExpected format: new_loan_id, old_loan_number, refi_payoff, refi_party`);
          return;
        }
        
        refis.push({
          new_loan_id: parseInt(parts[0]),
          old_loan_number: parts[1],
          refi_payoff: parseFloat(parts[2]),
          refi_party: parts[3],
          knackly_data: {
            data: {
              TaxLender: parts[3],
              StandardFees: {
                RefiParty: parts[3],
                RefiPayoff: parseFloat(parts[2]),
                RefiLoanNumber: parts[1],
              },
            },
          },
        });
      }
      
      const response = await bulkTriggerRefiAccounting(refis);
      
      setResult({
        success: response.success,
        message: `Processed ${response.total} refis: ${response.successful} successful, ${response.failed} failed`,
        details: response.results,
      });
      
      // Clear form on success
      if (response.success) {
        setBulkData('');
        if (onTrigger) onTrigger();
      }
      
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to bulk trigger refis',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h2>Manual Refi Trigger</h2>
      <p style={styles.description}>
        Manually trigger refi accounting for specific loan pairs. Use this for controlled testing or re-processing.
      </p>

      {/* Mode Selection */}
      <div style={styles.modeSelector}>
        <button
          className={`button ${mode === 'single' ? 'button-primary' : 'button-secondary'}`}
          onClick={() => setMode('single')}
        >
          Single Refi
        </button>
        <button
          className={`button ${mode === 'bulk' ? 'button-primary' : 'button-secondary'}`}
          onClick={() => setMode('bulk')}
        >
          Bulk Refis
        </button>
      </div>

      {/* Result Display */}
      {result && (
        <div className={result.success ? 'alert-success' : 'alert-error'} style={{ padding: '15px', marginBottom: '20px' }}>
          <strong>{result.success ? '✅ Success' : '❌ Error'}</strong>
          <p>{result.message}</p>
          
          {result.details && (
            <details style={styles.details}>
              <summary>View Details</summary>
              <pre style={styles.pre}>{JSON.stringify(result.details, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      {/* Single Refi Form */}
      {mode === 'single' && (
        <form onSubmit={handleSingleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              New Loan ID <span style={styles.required}>*</span>
            </label>
            <input
              type="number"
              name="new_loan_id"
              value={formData.new_loan_id}
              onChange={handleInputChange}
              placeholder="e.g., 25926"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Old Loan Number <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="old_loan_number"
              value={formData.old_loan_number}
              onChange={handleInputChange}
              placeholder="e.g., 233622307514540-TEST"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Refi Payoff Amount <span style={styles.required}>*</span>
            </label>
            <input
              type="number"
              step="0.01"
              name="refi_payoff"
              value={formData.refi_payoff}
              onChange={handleInputChange}
              placeholder="e.g., 3000.00"
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Refi Party <span style={styles.required}>*</span>
            </label>
            <select
              name="refi_party"
              value={formData.refi_party}
              onChange={handleInputChange}
              style={styles.select}
              required
            >
              <option value="Panacea Fund I, LLC">Panacea Fund I, LLC</option>
              <option value="Sombrero Property Tax Fund I, LLC">Sombrero Property Tax Fund I, LLC</option>
            </select>
          </div>

          <button
            type="submit"
            className="button button-success"
            disabled={loading}
            style={styles.submitButton}
          >
            {loading ? 'Processing...' : '🚀 Trigger Refi Accounting'}
          </button>
        </form>
      )}

      {/* Bulk Refi Form */}
      {mode === 'bulk' && (
        <form onSubmit={handleBulkSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Bulk Refi Data (CSV Format) <span style={styles.required}>*</span>
            </label>
            <div style={styles.helpText}>
              Format: <code>new_loan_id, old_loan_number, refi_payoff, refi_party</code>
              <br />
              Example: <code>25926, 233622307514540-TEST, 3000.00, Panacea Fund I, LLC</code>
            </div>
            <textarea
              value={bulkData}
              onChange={(e) => setBulkData(e.target.value)}
              placeholder="25926, 233622307514540-TEST, 3000.00, Panacea Fund I, LLC
25927, 233622307514541-TEST, 2500.00, Sombrero Property Tax Fund I, LLC"
              style={styles.textarea}
              rows={10}
              required
            />
          </div>

          <button
            type="submit"
            className="button button-success"
            disabled={loading}
            style={styles.submitButton}
          >
            {loading ? 'Processing...' : '🚀 Bulk Trigger Refi Accounting'}
          </button>
        </form>
      )}
    </div>
  );
};

const styles = {
  description: {
    color: 'var(--text-muted)',
    marginBottom: '20px',
  },
  modeSelector: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  form: {
    maxWidth: '600px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    fontSize: '14px',
    color: 'var(--text-primary)',
  },
  required: {
    color: '#dc3545',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
  },
  submitButton: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
  },
  helpText: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    marginBottom: '8px',
    padding: '8px',
    backgroundColor: 'var(--card-bg-hover)',
    borderRadius: '4px',
  },
  details: {
    marginTop: '10px',
  },
  pre: {
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '11px',
    overflow: 'auto',
    maxHeight: '300px',
  },
};

export default ManualTrigger;

